// Copyright (c) StrangeBee 2023

import React, { useState, ReactElement } from 'react';
import { Upload } from 'antd';
import { ICONS } from '@Constants/iconsApp';
import Resizer from 'react-image-file-resizer';
import { openMessage, useLocalMessage, getAvatarUrl } from '@Helpers/';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { classList } from '@Helpers/className';
import { THButton } from '@Molecules/Inputs/THButton/THButton';

import './UploadAvatar.scss';

export const AVATARB64_DEFAULT = undefined;
const AVATAR_PREFIX = /data:image\/(?:png|jpeg);base64,/;

// store the file formats authorized
const ALLOWED_FORMATS = ['image/jpeg', 'image/png'];

// store the maximum file size authorized
const MAX_SIZE_MO = 0.5;

// store this string in state to keep that the user has trigger the empty avatar button
export const AVATAR_EMPTY_VALUE = 'clear';

export const getUpdatedAvatar = (avatar?: string): string | undefined => {
    if (avatar === AVATAR_EMPTY_VALUE) {
        // if the user ask to empty his avatar,
        // send an empty string for the avatar to perform this action in backend
        return '';
    }

    if (avatar) {
        // if the user changed his avatar picture,
        // remove the AVATAR_PREFIX part before send it to the backend
        return avatar?.toString().replace(AVATAR_PREFIX, '');
    }
    // return the default value undefined, to keep avatar unchanged
    return AVATARB64_DEFAULT;
};

// convert a byte value in mega byte
const byteToMB = (numInByte: number): number => numInByte / 1024 ** 2;

export interface UploadAvatarProps {
    name?: string;
    avatar?: string | undefined;
    value?: string | undefined;
    onChange?: (value: string) => void;
    size?: 'large' | 'small';
}

const UploadAvatar = ({
    avatar,
    value = '',
    onChange = () => null,
    name,
    size = 'large',
}: UploadAvatarProps): ReactElement => {
    const i = useLocalMessage();
    const [imgLoading, setImgLoading] = useState<boolean>(false);

    const isFileValid = (file: File) => {
        if (!ALLOWED_FORMATS.includes(file.type)) {
            openMessage('error', i('account.avatarErrorFormat'));
            return false;
        }

        // convert octet to Mo
        if (byteToMB(file.size) > MAX_SIZE_MO) {
            openMessage('error', i('account.avatarErrorSize'));
            return false;
        }
        return true;
    };

    const onImageChange = (newValue: string | Blob | ProgressEvent<FileReader>) => {
        onChange(newValue.toString());
    };

    const transformFile = (fileData: any): void => {
        const fileInfo: string[] = (fileData?.type || '/jpeg').split('/');
        Resizer.imageFileResizer(fileData, 100, 100, fileInfo[1].toLocaleUpperCase(), 100, 0, onImageChange, 'base64');
    };

    const onUpload = ({ file }: any): void => {
        setImgLoading(true);
        // the timeout is necessary to return a promise required by the avatar component
        setTimeout(() => {
            transformFile(file);
            setImgLoading(false);
        }, 0);
    };

    const getAvatarSrc = () => {
        if (value === AVATAR_EMPTY_VALUE) {
            return '';
        }
        if (value) {
            return value.toString();
        } else if (avatar) {
            return getAvatarUrl(avatar);
        }
        return '';
    };

    const emptyAvatar = (): void => {
        onChange(AVATAR_EMPTY_VALUE);
    };

    return (
        <div className={classList('upload-avatar__container', { small: size === 'small' })}>
            <div className={classList('upload-avatar', { small: size === 'small' })}>
                <Upload
                    showUploadList={false}
                    customRequest={onUpload}
                    beforeUpload={isFileValid}
                    className="upload-avatar__avatar"
                >
                    {imgLoading ? (
                        <ICONS.LoadingIcon />
                    ) : (
                        <UserAvatar name={name} avatar={getAvatarSrc()} size="large" />
                    )}
                </Upload>

                <div className="upload-avatar__edit">
                    <ICONS.EditIcon />
                </div>
            </div>

            {(avatar || value !== AVATAR_EMPTY_VALUE) && (
                <THButton className="upload-avatar__clear" onClick={emptyAvatar} type="link">
                    {i('account.clear')}
                </THButton>
            )}
        </div>
    );
};

export default UploadAvatar;
